import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { patientsAPI, formsAPI, prescriptionsAPI, aiAPI, labRequestsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ICD10Display } from '../components/ICD10Codes';
import LabRequestDialog from '../components/LabRequestDialog';
import PatientHistoryTab from '../components/PatientHistoryTab';
import PatientLabsTab from '../components/PatientLabsTab';
import PatientEncountersTab from '../components/PatientEncountersTab';
import { cn, formatDate, formatDateTime, calculateAge, getCategoryColor, formatRelativeTime } from '../lib/utils';
import { generatePatientSummaryPDF, generatePrescriptionPDF, generateAIReportPDF } from '../lib/pdfGenerator';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ArrowLeft,
  Edit,
  MoreVertical,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Calendar,
  User,
  Heart,
  AlertTriangle,
  FileText,
  Stethoscope,
  Pill,
  TestTube,
  Scan,
  LogOut,
  Paperclip,
  Plus,
  ClipboardList,
  Loader2,
  Sparkles,
  Receipt,
  Download,
  FileDown,
  Eye,
  Upload,
  History,
  FileOutput,
  Check,
  FlaskConical,
} from 'lucide-react';
import { toast } from 'sonner';

export default function PatientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  
  const [patient, setPatient] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [prescriptionDialogOpen, setPrescriptionDialogOpen] = useState(false);
  const [prescriptionMeds, setPrescriptionMeds] = useState([{ 
    name: '', 
    dose: '', 
    frequency: '', 
    customFrequency: '',
    duration: '', 
    quantity: '', 
    route: 'oral',
    customRoute: '',
    instructions: '' 
  }]);
  const [prescriptionNotes, setPrescriptionNotes] = useState('');
  const [generatingRx, setGeneratingRx] = useState(false);
  const [savingRx, setSavingRx] = useState(false);
  const [prescriptionHistoryOpen, setPrescriptionHistoryOpen] = useState(false);
  const fileInputRef = useState(null);

  // Lab Request state
  const [labRequestDialogOpen, setLabRequestDialogOpen] = useState(false);
  const [labRequests, setLabRequests] = useState([]);

  // AI Report Generator state
  const [aiReportDialogOpen, setAiReportDialogOpen] = useState(false);
  const [aiReportPreviewOpen, setAiReportPreviewOpen] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [aiReportType, setAiReportType] = useState('');
  const [specialistType, setSpecialistType] = useState('');
  const [aiReportInclusions, setAiReportInclusions] = useState({
    demographics: true,
    allergies: true,
    medications: true,
    active_problems: true,
    encounters: true,
    clinical_notes: true,
    prescriptions: true,
    attachments: true,
  });
  const [aiReportAdditionalNotes, setAiReportAdditionalNotes] = useState('');
  const [generatedReport, setGeneratedReport] = useState(null);
  const [editedReportText, setEditedReportText] = useState('');

  // Report type options
  const reportTypeOptions = [
    { value: 'medical_transfer', label: 'Medical Transfer Report' },
    { value: 'fit_to_fly', label: 'Fit-to-Fly Certificate' },
    { value: 'hospital_referral', label: 'Hospital Referral Letter' },
    { value: 'specialist_referral', label: 'Specialist Referral Letter' },
    { value: 'fitness_certificate', label: 'Fitness Certificate' },
  ];

  // Specialist type options
  const specialistTypeOptions = [
    'Cardiologist', 'Neurologist', 'Orthopedic Surgeon', 'Gastroenterologist',
    'Pulmonologist', 'Endocrinologist', 'Nephrologist', 'Rheumatologist',
    'Dermatologist', 'Ophthalmologist', 'ENT Specialist', 'Urologist',
    'Oncologist', 'Hematologist', 'Psychiatrist', 'General Surgeon',
    'Vascular Surgeon', 'Plastic Surgeon', 'Pediatrician', 'Obstetrician/Gynecologist',
    'Other'
  ];

  // Common frequency options
  const frequencyOptions = [
    'Once daily',
    'Twice daily',
    'Three times daily',
    'Four times daily',
    'Every 4 hours',
    'Every 6 hours',
    'Every 8 hours',
    'Every 12 hours',
    'Once weekly',
    'As needed (PRN)',
    'At bedtime',
    'With meals',
    'Before meals',
    'After meals',
    'Other (custom)'
  ];

  // Route options
  const routeOptions = [
    'Oral',
    'Sublingual',
    'Topical',
    'Inhalation',
    'Injection (IM)',
    'Injection (IV)',
    'Injection (SC)',
    'Rectal',
    'Ophthalmic',
    'Otic',
    'Nasal',
    'Transdermal',
    'Custom'
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const [patientRes, timelineRes, attachmentsRes, templatesRes, prescriptionsRes, labRequestsRes] = await Promise.all([
        patientsAPI.getById(id),
        patientsAPI.getTimeline(id),
        patientsAPI.getAttachments(id),
        formsAPI.getTemplates({ is_active: true }),
        prescriptionsAPI.getByPatient(id).catch(() => ({ data: [] })),
        labRequestsAPI.getByPatient(id).catch(() => ({ data: [] })),
      ]);
      setPatient(patientRes.data);
      setTimeline(timelineRes.data);
      setAttachments(attachmentsRes.data);
      setTemplates(templatesRes.data);
      setPrescriptions(prescriptionsRes.data || []);
      setLabRequests(labRequestsRes.data || []);
    } catch (err) {
      toast.error('Failed to load patient data');
      navigate('/app/patients');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleDelete = async () => {
    try {
      await patientsAPI.delete(id);
      toast.success('Patient deleted');
      navigate('/app/patients');
    } catch (err) {
      toast.error('Failed to delete patient');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/upload?patient_id=${id}&file_type=document`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('emr_auth_token')}`,
        },
        body: formData,
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      toast.success('File uploaded successfully');
      fetchData(); // Refresh to show new file in timeline
    } catch (err) {
      toast.error('Failed to upload file');
    }
    setUploading(false);
    // Reset file input
    event.target.value = '';
  };

  const handleGenerateSummaryPDF = async () => {
    setGeneratingPDF(true);
    try {
      const response = await patientsAPI.getSummary(id);
      console.log('Summary data:', response.data);
      // Add user's phone to the summary data
      await generatePatientSummaryPDF({
        ...response.data,
        generated_by_phone: user?.personal_phone
      });
      toast.success('Clinical summary PDF generated!');
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to generate PDF: ' + (err.message || 'Unknown error'));
    }
    setGeneratingPDF(false);
  };

  const handleAddMedication = () => {
    setPrescriptionMeds([...prescriptionMeds, { 
      name: '', 
      dose: '', 
      frequency: '', 
      customFrequency: '',
      duration: '', 
      quantity: '', 
      route: 'oral',
      customRoute: '',
      instructions: '' 
    }]);
  };

  const handleRemoveMedication = (index) => {
    if (prescriptionMeds.length > 1) {
      setPrescriptionMeds(prescriptionMeds.filter((_, i) => i !== index));
    }
  };

  const handleMedicationChange = (index, field, value) => {
    const updated = [...prescriptionMeds];
    updated[index][field] = value;
    // If selecting "Other (custom)", clear the frequency to show custom input
    if (field === 'frequency' && value === 'Other (custom)') {
      updated[index].customFrequency = '';
    }
    // If selecting "Custom" route, clear the custom route to show custom input
    if (field === 'route' && value === 'custom') {
      updated[index].customRoute = '';
    }
    setPrescriptionMeds(updated);
  };

  const getEffectiveFrequency = (med) => {
    if (med.frequency === 'Other (custom)') {
      return med.customFrequency || '';
    }
    return med.frequency;
  };

  const getEffectiveRoute = (med) => {
    if (med.route === 'custom') {
      return med.customRoute || '';
    }
    return med.route;
  };

  const handleSaveAndGeneratePrescription = async () => {
    // Validate at least one medication with name and frequency
    const validMeds = prescriptionMeds.filter(m => m.name.trim());
    if (validMeds.length === 0) {
      toast.error('Please add at least one medication');
      return;
    }

    // Check that each medication has a frequency
    for (const med of validMeds) {
      const freq = getEffectiveFrequency(med);
      if (!freq) {
        toast.error('Please specify frequency for all medications');
        return;
      }
      if (med.route === 'custom' && !med.customRoute?.trim()) {
        toast.error('Please enter a custom route for all medications');
        return;
      }
    }

    setSavingRx(true);
    try {
      // Prepare medications with effective frequency and route
      const medsToSave = validMeds.map(med => ({
        name: med.name,
        dose: med.dose,
        frequency: getEffectiveFrequency(med),
        duration: med.duration,
        quantity: med.quantity,
        route: getEffectiveRoute(med),
        instructions: med.instructions
      }));

      // Save to database
      const response = await prescriptionsAPI.create({
        patient_id: id,
        medications: medsToSave,
        notes: prescriptionNotes
      });

      toast.success('Prescription saved!');

      // Generate PDF
      await generatePrescriptionPDF({
        patient: patient,
        prescription_number: response.data.prescription_number,
        medications: medsToSave,
        prescriber: user?.full_name || 'Physician',
        prescriber_title: user?.professional_title || 'M.D.',
        date: new Date(),
        notes: prescriptionNotes
      });

      toast.success('Prescription PDF downloaded!');
      setPrescriptionDialogOpen(false);
      
      // Reset form
      setPrescriptionMeds([{ 
        name: '', 
        dose: '', 
        frequency: '', 
        customFrequency: '',
        duration: '', 
        quantity: '', 
        route: 'oral',
        customRoute: '',
        instructions: '' 
      }]);
      setPrescriptionNotes('');
      
      // Refresh prescriptions list
      fetchData();
    } catch (err) {
      console.error('Prescription save error:', err);
      toast.error('Failed to save prescription: ' + (err.response?.data?.detail || err.message));
    }
    setSavingRx(false);
  };

  const handleGeneratePrescription = async () => {
    // Validate at least one medication
    const validMeds = prescriptionMeds.filter(m => m.name.trim());
    if (validMeds.length === 0) {
      toast.error('Please add at least one medication');
      return;
    }

    // Prepare medications with effective frequency and route
    const medsToGenerate = validMeds.map(med => ({
      name: med.name,
      dose: med.dose,
      frequency: getEffectiveFrequency(med),
      duration: med.duration,
      quantity: med.quantity,
      route: getEffectiveRoute(med),
      instructions: med.instructions
    }));

    setGeneratingRx(true);
    try {
      await generatePrescriptionPDF({
        patient: patient,
        medications: medsToGenerate,
        prescriber: user?.full_name || 'Physician',
        prescriber_title: user?.professional_title || 'M.D.',
        prescriber_phone: user?.personal_phone,
        date: new Date(),
        notes: prescriptionNotes
      });
      toast.success('Prescription PDF generated!');
    } catch (err) {
      console.error('Prescription generation error:', err);
      toast.error('Failed to generate prescription');
    }
    setGeneratingRx(false);
  };

  // AI Report Generation handlers
  const handleGenerateAIReport = async () => {
    if (!aiReportType) {
      toast.error('Please select a report type');
      return;
    }
    
    if (aiReportType === 'specialist_referral' && !specialistType) {
      toast.error('Please select a specialist type');
      return;
    }

    setGeneratingReport(true);
    try {
      const response = await aiAPI.generateReport({
        patient_id: id,
        report_type: aiReportType,
        specialist_type: aiReportType === 'specialist_referral' ? specialistType : null,
        include_demographics: aiReportInclusions.demographics,
        include_allergies: aiReportInclusions.allergies,
        include_medications: aiReportInclusions.medications,
        include_active_problems: aiReportInclusions.active_problems,
        include_encounters: aiReportInclusions.encounters,
        include_clinical_notes: aiReportInclusions.clinical_notes,
        include_prescriptions: aiReportInclusions.prescriptions,
        include_attachments: aiReportInclusions.attachments,
        additional_notes: aiReportAdditionalNotes,
      });

      setGeneratedReport(response.data);
      setEditedReportText(response.data.generated_text);
      setAiReportDialogOpen(false);
      setAiReportPreviewOpen(true);
      toast.success('Report generated successfully!');
    } catch (err) {
      console.error('AI Report generation error:', err);
      toast.error('Failed to generate report: ' + (err.response?.data?.detail || err.message));
    }
    setGeneratingReport(false);
  };

  const handleDownloadAIReport = async () => {
    if (!generatedReport) return;

    try {
      await generateAIReportPDF({
        patient_name: `${patient.first_name} ${patient.last_name}`,
        report_type: generatedReport.report_type,
        specialist_type: generatedReport.specialist_type,
        report_text: editedReportText,
        generated_by: user?.full_name || 'Physician',
        generated_by_phone: user?.personal_phone,
        generated_at: generatedReport.created_at,
      });

      // Finalize the report with edited text
      await aiAPI.finalizeReport(generatedReport.id, editedReportText);
      
      toast.success('Report downloaded successfully!');
      setAiReportPreviewOpen(false);
      
      // Reset form
      setAiReportType('');
      setSpecialistType('');
      setAiReportAdditionalNotes('');
      setGeneratedReport(null);
      setEditedReportText('');
    } catch (err) {
      console.error('Report download error:', err);
      toast.error('Failed to download report');
    }
  };

  const handleResetAIReportForm = () => {
    setAiReportType('');
    setSpecialistType('');
    setAiReportInclusions({
      demographics: true,
      allergies: true,
      medications: true,
      active_problems: true,
      encounters: true,
      clinical_notes: true,
      prescriptions: true,
      attachments: true,
    });
    setAiReportAdditionalNotes('');
  };

  const getCategoryIcon = (category) => {
    const icons = {
      registration: ClipboardList,
      consultation: Stethoscope,
      progress: FileText,
      prescription: Pill,
      lab: TestTube,
      lab_results: FlaskConical,
      lab_document: Upload,
      imaging: Scan,
      discharge: LogOut,
      attachment: Paperclip,
      clinical_note: Sparkles,
      billing: Receipt,
      encounter: Stethoscope,
      appointment: Calendar,
    };
    return icons[category] || FileText;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">Loading patient data...</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-4">
          <User className="h-8 w-8 text-slate-500" strokeWidth={1.5} />
        </div>
        <p className="text-slate-500 mb-4">Patient not found</p>
        <Button onClick={() => navigate('/app/patients')} className="rounded-full btn-primary-glow">
          Back to Patients
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn p-2" data-testid="patient-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/app/patients')} 
            className="rounded-xl hover:bg-slate-50"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border border-primary/10">
              <span className="font-bold text-xl text-primary">
                {patient.first_name[0]}{patient.last_name[0]}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-primary mb-1">Patient Profile</p>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                {patient.first_name} {patient.last_name}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <code className="text-xs bg-slate-50 px-2.5 py-1 rounded-lg font-mono text-slate-500">{patient.patient_id}</code>
                <Badge className="bg-slate-50 text-slate-900 border border-slate-200 rounded-lg">{patient.gender}</Badge>
                {patient.blood_type && (
                  <Badge className="bg-red-50 text-red-600 border border-red-200 rounded-lg">{patient.blood_type}</Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:ml-auto flex-wrap">
          {/* AI Clinical Notes */}
          {hasPermission('clinical_notes:create') && (
            <Button 
              onClick={() => navigate(`/app/patients/${id}/clinical-notes`)}
              className="gap-2 rounded-full bg-slate-50 text-slate-900 hover:bg-slate-100 border border-slate-200"
              data-testid="clinical-notes-btn"
            >
              <Sparkles className="h-4 w-4" />
              AI Notes
            </Button>
          )}

          {/* AI Report Generator */}
          {hasPermission('clinical_notes:create') && (
            <Button 
              onClick={() => {
                handleResetAIReportForm();
                setAiReportDialogOpen(true);
              }}
              className="gap-2 rounded-full bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20"
              data-testid="ai-report-btn"
            >
              <FileOutput className="h-4 w-4" />
              AI Report
            </Button>
          )}

          {/* Invoices */}
          {hasPermission('invoices:read') && (
            <Button 
              onClick={() => navigate(`/app/patients/${id}/invoices`)}
              className="gap-2 rounded-full bg-slate-50 text-slate-900 hover:bg-slate-100 border border-slate-200"
              data-testid="invoices-btn"
            >
              <Receipt className="h-4 w-4" />
              Invoices
            </Button>
          )}

          {/* New Submission */}
          {hasPermission('submissions:create') && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="gap-2 rounded-full btn-primary-glow" data-testid="new-submission-btn">
                  <Plus className="h-4 w-4" />
                  New Entry
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white border-slate-200">
                {templates.map((template) => (
                  <DropdownMenuItem
                    key={template.id}
                    onClick={() => navigate(`/app/forms/submit/${template.id}?patient=${id}`)}
                    className="hover:bg-slate-50"
                    data-testid={`template-${template.id}`}
                  >
                    {template.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Upload File Button */}
          {hasPermission('patients:update') && (
            <>
              <input
                type="file"
                id="file-upload"
                className="hidden"
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
              />
              <Button 
                className="gap-2 rounded-full bg-slate-50 text-slate-900 hover:bg-slate-100 border border-slate-200"
                onClick={() => document.getElementById('file-upload').click()}
                disabled={uploading}
                data-testid="upload-file-btn"
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {uploading ? 'Uploading...' : 'Upload File'}
              </Button>
            </>
          )}

          {/* Actions Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-xl hover:bg-slate-50" data-testid="patient-actions-btn">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white border-slate-200">
              <DropdownMenuItem 
                onClick={handleGenerateSummaryPDF}
                disabled={generatingPDF}
                className="hover:bg-slate-50"
                data-testid="generate-summary-btn"
              >
                <FileDown className="h-4 w-4 mr-2" />
                {generatingPDF ? 'Generating...' : 'Download Clinical Summary'}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setPrescriptionDialogOpen(true)}
                className="hover:bg-slate-50"
                data-testid="create-prescription-btn"
              >
                <Pill className="h-4 w-4 mr-2" />
                Create Prescription
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setLabRequestDialogOpen(true)}
                className="hover:bg-slate-50"
                data-testid="create-lab-request-btn"
              >
                <FlaskConical className="h-4 w-4 mr-2" />
                Generate Lab Request
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setPrescriptionHistoryOpen(true)}
                className="hover:bg-slate-50"
                data-testid="prescription-history-btn"
              >
                <History className="h-4 w-4 mr-2" />
                Prescription History ({prescriptions.length})
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-100" />
              {hasPermission('patients:update') && (
                <DropdownMenuItem onClick={() => navigate(`/app/patients/${id}/edit`)} className="hover:bg-slate-50" data-testid="edit-patient-btn">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Patient
                </DropdownMenuItem>
              )}
              {hasPermission('patients:delete') && (
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-600 hover:bg-red-500/10"
                  onClick={() => setDeleteDialogOpen(true)}
                  data-testid="delete-patient-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Patient
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Patient Info */}
        <div className="premium-card lg:col-span-1 overflow-hidden">
          <div className="p-5 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Patient Information</h2>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-slate-50">
                <Calendar className="h-4 w-4 text-slate-500" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Date of Birth</p>
                <p className="font-medium text-slate-900">{formatDate(patient.date_of_birth)} ({calculateAge(patient.date_of_birth)} years)</p>
              </div>
            </div>

            {patient.phone && (
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-slate-50">
                  <Phone className="h-4 w-4 text-slate-500" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Phone</p>
                  <p className="font-medium text-slate-900">{patient.phone}</p>
                </div>
              </div>
            )}

            {patient.email && (
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-slate-50">
                  <Mail className="h-4 w-4 text-slate-500" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Email</p>
                  <p className="font-medium text-slate-900">{patient.email}</p>
                </div>
              </div>
            )}

            {patient.address && (
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-slate-50">
                  <MapPin className="h-4 w-4 text-slate-500" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Address</p>
                  <p className="font-medium text-slate-900">{patient.address}</p>
                </div>
              </div>
            )}

            {patient.emergency_contact && (
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-slate-50">
                  <User className="h-4 w-4 text-slate-500" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Emergency Contact</p>
                  <p className="font-medium text-slate-900">{patient.emergency_contact}</p>
                </div>
              </div>
            )}

            <Separator className="bg-slate-100" />

            {/* Allergies */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-amber-600" strokeWidth={1.5} />
                </div>
                <span className="text-sm font-semibold text-slate-900">Allergies</span>
              </div>
              {patient.allergies && patient.allergies.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {patient.allergies.map((allergy, idx) => (
                    <Badge key={idx} className="bg-amber-50 text-amber-600 border border-amber-200 rounded-lg">
                      {allergy}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No known allergies</p>
              )}
            </div>

            {/* Active Problems */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <Heart className="h-4 w-4 text-red-600" strokeWidth={1.5} />
                </div>
                <span className="text-sm font-semibold text-slate-900">Active Problems / Diagnoses</span>
              </div>
              {patient.active_problems && patient.active_problems.length > 0 ? (
                <div className="space-y-2">
                  {patient.active_problems.map((problem, idx) => (
                    <ICD10Display key={idx} diagnosis={problem} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">None documented</p>
              )}
            </div>

            {/* Medications */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Pill className="h-4 w-4 text-blue-600" strokeWidth={1.5} />
                </div>
                <span className="text-sm font-semibold text-slate-900">Current Medications</span>
              </div>
              {patient.medications && patient.medications.length > 0 ? (
                <div className="space-y-1">
                  {patient.medications.map((med, idx) => (
                    <p key={idx} className="text-sm text-slate-900">• {med}</p>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">None documented</p>
              )}
            </div>
          </div>
        </div>

        {/* Timeline, History & Files */}
        <div className="premium-card lg:col-span-2 overflow-hidden">
          <Tabs defaultValue="timeline">
            <div className="p-5 border-b border-slate-100">
              <TabsList className="bg-slate-50">
                <TabsTrigger value="timeline" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-lg" data-testid="timeline-tab">Timeline</TabsTrigger>
                <TabsTrigger value="encounters" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-lg" data-testid="encounters-tab">Encounters</TabsTrigger>
                <TabsTrigger value="history" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-lg" data-testid="history-tab">History</TabsTrigger>
                <TabsTrigger value="labs" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-lg" data-testid="labs-tab">Labs</TabsTrigger>
                <TabsTrigger value="files" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-lg" data-testid="files-tab">Files ({attachments.length})</TabsTrigger>
              </TabsList>
            </div>
            <div className="p-5">
              <TabsContent value="timeline" className="mt-0">
                <ScrollArea className="h-[500px] pr-4">
                  {timeline.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-4">
                        <FileText className="h-8 w-8 text-slate-500" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-1">No records yet</h3>
                      <p className="text-slate-500 mb-4">
                        Start by creating a new entry for this patient
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {timeline.map((item, index) => {
                        const IconComponent = getCategoryIcon(item.category || item.type);
                        return (
                          <div key={item.id} className="flex gap-4" data-testid={`timeline-item-${item.id}`}>
                            <div className={cn(
                              'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0',
                              getCategoryColor(item.category || item.type)
                            )}>
                              <IconComponent className="h-5 w-5" />
                            </div>
                            <div 
                              className="flex-1 p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-slate-200 cursor-pointer transition-all"
                              onClick={() => {
                                setSelectedEntry(item);
                                setEntryDialogOpen(true);
                              }}
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <h4 className="font-medium text-slate-900">
                                      {item.form_name || item.filename || item.note_type?.toUpperCase() || item.invoice_number}
                                    </h4>
                                    <Badge className={cn('text-xs rounded-lg', getCategoryColor(item.category || item.type))}>
                                      {item.category || item.type}
                                    </Badge>
                                    {item.status && (
                                      <Badge className="text-xs bg-slate-50 text-slate-500 border border-slate-200 rounded-lg">
                                        {item.status}
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-slate-500">
                                    by {item.submitted_by || item.uploaded_by}
                                  </p>
                                  
                                  {/* Show submission data preview */}
                                  {item.data && Object.keys(item.data).length > 0 && (
                                    <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                                      {Object.entries(item.data).filter(([, v]) => v && String(v).trim()).slice(0, 2).map(([key, value]) => (
                                        <div key={key} className="text-sm">
                                          <span className="text-slate-500 capitalize">
                                            {key.replace(/_/g, ' ')}:
                                          </span>{' '}
                                          <span className="font-medium text-slate-900">
                                            {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value).slice(0, 80)}
                                            {String(value).length > 80 && '...'}
                                          </span>
                                        </div>
                                      ))}
                                      {Object.entries(item.data).filter(([, v]) => v && String(v).trim()).length > 2 && (
                                        <p className="text-xs text-primary mt-2 flex items-center gap-1">
                                          <Eye className="h-3 w-3" />
                                          Click to view all details
                                        </p>
                                      )}
                                    </div>
                                  )}

                                  {/* Invoice total */}
                                  {item.total !== undefined && (
                                    <div className="mt-2">
                                      <span className="text-lg font-bold text-primary">${item.total.toFixed(2)}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                  <span className="text-xs text-slate-500 whitespace-nowrap">
                                    {formatRelativeTime(item.timestamp)}
                                  </span>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="h-8 w-8 p-0 rounded-lg hover:bg-slate-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedEntry(item);
                                      setEntryDialogOpen(true);
                                    }}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>

              <TabsContent value="encounters" className="mt-0" data-testid="encounters-tab-content">
                <PatientEncountersTab patientId={id} patient={patient} />
              </TabsContent>

              <TabsContent value="history" className="mt-0" data-testid="history-tab-content">
                <PatientHistoryTab patientId={id} patientName={`${patient.first_name} ${patient.last_name}`} />
              </TabsContent>

              <TabsContent value="labs" className="mt-0" data-testid="labs-tab-content">
                <PatientLabsTab patientId={id} patient={patient} />
              </TabsContent>

              <TabsContent value="files" className="mt-0">
                <ScrollArea className="h-[500px]">
                  {attachments.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-4">
                        <Paperclip className="h-8 w-8 text-slate-500" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-1">No files attached</h3>
                      <p className="text-slate-500 mb-4">
                        Upload lab results, images, or other documents
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-3">
                      {attachments.map((file) => (
                        <div key={file.id} className="p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-slate-200 transition-all flex items-center gap-4" data-testid={`file-${file.id}`}>
                          <div className="p-2.5 rounded-xl bg-slate-50">
                            <Paperclip className="h-5 w-5 text-slate-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate text-slate-900">{file.filename}</p>
                            <p className="text-sm text-slate-500">
                              Uploaded by {file.uploaded_by_name} • {formatDateTime(file.uploaded_at)}
                            </p>
                          </div>
                          <Button 
                            className="rounded-full bg-slate-50 text-slate-900 hover:bg-slate-100 border border-slate-200" 
                            size="sm"
                            onClick={() => {
                              // Open file download in new tab
                              const token = localStorage.getItem('emr_auth_token');
                              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${file.id}?token=${token}`, '_blank');
                            }}
                            data-testid={`download-file-${file.id}`}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>

      {/* Entry Detail Dialog */}
      <Dialog open={entryDialogOpen} onOpenChange={setEntryDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              {selectedEntry?.form_name || selectedEntry?.filename || selectedEntry?.note_type?.toUpperCase() || selectedEntry?.invoice_number || 'Entry Details'}
              {selectedEntry?.category && (
                <Badge className={cn('text-xs rounded-lg', getCategoryColor(selectedEntry.category || selectedEntry.type))}>
                  {selectedEntry.category || selectedEntry.type}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedEntry?.submitted_by && `Submitted by ${selectedEntry.submitted_by}`}
              {selectedEntry?.timestamp && ` • ${formatDateTime(selectedEntry.timestamp)}`}
            </DialogDescription>
          </DialogHeader>
          
          {selectedEntry && (
            <div className="space-y-4 mt-4">
              {/* Form submission data */}
              {selectedEntry.data && Object.keys(selectedEntry.data).length > 0 && (
                <div className="space-y-4">
                  {Object.entries(selectedEntry.data).map(([key, value]) => (
                    <div key={key} className="border-b border-slate-100 pb-3 last:border-0">
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1">
                        {key.replace(/_/g, ' ')}
                      </label>
                      <div className="text-sm text-slate-900 whitespace-pre-wrap">
                        {typeof value === 'boolean' ? (
                          <Badge className={value ? "bg-primary/10 text-primary" : "bg-slate-50 text-slate-500"}>
                            {value ? 'Yes' : 'No'}
                          </Badge>
                        ) : (
                          String(value) || <span className="text-slate-500 italic">Not provided</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Invoice details */}
              {selectedEntry.total !== undefined && (
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Total Amount</span>
                    <span className="text-2xl font-bold text-primary">${selectedEntry.total.toFixed(2)}</span>
                  </div>
                  {selectedEntry.status && (
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-slate-500">Status</span>
                      <Badge className="bg-slate-50 text-slate-500 border border-slate-200 rounded-lg">{selectedEntry.status}</Badge>
                    </div>
                  )}
                </div>
              )}
              
              {/* Attachment info */}
              {selectedEntry.filename && (
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center gap-3">
                  <Paperclip className="h-5 w-5 text-slate-500" />
                  <div>
                    <p className="font-medium text-slate-900">{selectedEntry.filename}</p>
                    <p className="text-sm text-slate-500">
                      {selectedEntry.file_type} • Uploaded {formatDateTime(selectedEntry.timestamp)}
                    </p>
                  </div>
                </div>
              )}
              
              {/* No data message */}
              {!selectedEntry.data && !selectedEntry.total && !selectedEntry.filename && (
                <p className="text-center text-slate-500 py-8">
                  No additional details available for this entry.
                </p>
              )}
            </div>
          )}
          
          <div className="flex justify-end mt-4">
            <Button variant="ghost" onClick={() => setEntryDialogOpen(false)} className="rounded-full">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Delete Patient</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {patient.first_name} {patient.last_name}? 
              This action cannot be undone and will remove all associated records.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="ghost" onClick={() => setDeleteDialogOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button className="rounded-full bg-red-500/10 text-red-600 hover:bg-red-500/20" onClick={handleDelete} data-testid="confirm-delete-btn">
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Prescription Dialog */}
      <Dialog open={prescriptionDialogOpen} onOpenChange={setPrescriptionDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Pill className="h-4 w-4 text-primary" />
              </div>
              Create Prescription
            </DialogTitle>
            <DialogDescription>
              Create a prescription for {patient.first_name} {patient.last_name}. Add multiple medications and generate a downloadable PDF.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Medications */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold text-slate-900">Medications ({prescriptionMeds.length})</Label>
                <Button type="button" className="rounded-full bg-slate-50 text-slate-900 hover:bg-slate-100 border border-slate-200" size="sm" onClick={handleAddMedication}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Medication
                </Button>
              </div>
              
              {prescriptionMeds.map((med, index) => (
                <div key={index} className="p-4 rounded-xl bg-slate-50 border-l-4 border-l-primary">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-primary">Medication {index + 1}</span>
                      {prescriptionMeds.length > 1 && (
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600 h-8 w-8 p-0 hover:bg-red-500/10 rounded-lg"
                          onClick={() => handleRemoveMedication(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="md:col-span-2">
                        <Label htmlFor={`med-name-${index}`} className="text-xs font-semibold uppercase tracking-wider text-slate-500">Medication Name *</Label>
                        <Input
                          id={`med-name-${index}`}
                          placeholder="e.g., Amoxicillin 500mg capsules"
                          value={med.name}
                          onChange={(e) => handleMedicationChange(index, 'name', e.target.value)}
                          className="mt-1 rounded-xl bg-white border-slate-200"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`med-route-${index}`} className="text-xs font-semibold uppercase tracking-wider text-slate-500">Route</Label>
                        <Select
                          value={med.route}
                          onValueChange={(value) => handleMedicationChange(index, 'route', value)}
                        >
                          <SelectTrigger id={`med-route-${index}`} className="mt-1 rounded-xl bg-white border-slate-200">
                            <SelectValue placeholder="Select route" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-slate-200">
                            {routeOptions.map(route => (
                              <SelectItem key={route} value={route.toLowerCase()}>{route}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {med.route === 'custom' && (
                        <div>
                          <Label htmlFor={`med-custom-route-${index}`} className="text-xs font-semibold uppercase tracking-wider text-slate-500">Enter Custom Route *</Label>
                          <Input
                            id={`med-custom-route-${index}`}
                            data-testid={`med-custom-route-${index}`}
                            placeholder="e.g., Intravaginal, Buccal, Nebulized"
                            value={med.customRoute}
                            onChange={(e) => handleMedicationChange(index, 'customRoute', e.target.value)}
                            className="mt-1 rounded-xl bg-white border-slate-200"
                          />
                        </div>
                      )}
                      <div>
                        <Label htmlFor={`med-dose-${index}`} className="text-xs font-semibold uppercase tracking-wider text-slate-500">Dose/Strength</Label>
                        <Input
                          id={`med-dose-${index}`}
                          placeholder="e.g., 1 capsule, 5ml"
                          value={med.dose}
                          onChange={(e) => handleMedicationChange(index, 'dose', e.target.value)}
                          className="mt-1 rounded-xl bg-white border-slate-200"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`med-freq-${index}`} className="text-xs font-semibold uppercase tracking-wider text-slate-500">Frequency *</Label>
                        <Select
                          value={med.frequency}
                          onValueChange={(value) => handleMedicationChange(index, 'frequency', value)}
                        >
                          <SelectTrigger id={`med-freq-${index}`} className="mt-1 rounded-xl bg-white border-slate-200">
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-slate-200">
                            {frequencyOptions.map(freq => (
                              <SelectItem key={freq} value={freq}>{freq}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {med.frequency === 'Other (custom)' && (
                        <div>
                          <Label htmlFor={`med-custom-freq-${index}`} className="text-xs font-semibold uppercase tracking-wider text-slate-500">Custom Frequency *</Label>
                          <Input
                            id={`med-custom-freq-${index}`}
                            placeholder="Enter custom frequency"
                            value={med.customFrequency}
                            onChange={(e) => handleMedicationChange(index, 'customFrequency', e.target.value)}
                            className="mt-1 rounded-xl bg-white border-slate-200"
                          />
                        </div>
                      )}
                      <div>
                        <Label htmlFor={`med-duration-${index}`} className="text-xs font-semibold uppercase tracking-wider text-slate-500">Duration</Label>
                        <Input
                          id={`med-duration-${index}`}
                          placeholder="e.g., 7 days, 2 weeks"
                          value={med.duration}
                          onChange={(e) => handleMedicationChange(index, 'duration', e.target.value)}
                          className="mt-1 rounded-xl bg-white border-slate-200"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`med-qty-${index}`} className="text-xs font-semibold uppercase tracking-wider text-slate-500">Quantity</Label>
                        <Input
                          id={`med-qty-${index}`}
                          placeholder="e.g., 21 capsules"
                          value={med.quantity}
                          onChange={(e) => handleMedicationChange(index, 'quantity', e.target.value)}
                          className="mt-1 rounded-xl bg-white border-slate-200"
                        />
                      </div>
                      <div className="md:col-span-3">
                        <Label htmlFor={`med-inst-${index}`} className="text-xs font-semibold uppercase tracking-wider text-slate-500">Special Instructions (SIG)</Label>
                        <Input
                          id={`med-inst-${index}`}
                          placeholder="e.g., Take with food, avoid alcohol"
                          value={med.instructions}
                          onChange={(e) => handleMedicationChange(index, 'instructions', e.target.value)}
                          className="mt-1 rounded-xl bg-white border-slate-200"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Additional Notes */}
            <div>
              <Label htmlFor="rx-notes" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Additional Notes for Pharmacist</Label>
              <Textarea
                id="rx-notes"
                placeholder="Any additional instructions, warnings, or notes for the pharmacist..."
                value={prescriptionNotes}
                onChange={(e) => setPrescriptionNotes(e.target.value)}
                rows={3}
                className="mt-1 rounded-xl bg-white border-slate-200"
              />
            </div>
            
            {/* Prescriber Info */}
            <div className="bg-slate-50 p-4 rounded-xl flex items-center justify-between border border-slate-100">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Prescribing Physician</p>
                <p className="font-medium text-slate-900">{user?.full_name || 'Physician'}, {user?.professional_title || 'M.D.'}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Date</p>
                <p className="font-medium text-slate-900">{formatDate(new Date())}</p>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={() => setPrescriptionDialogOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleGeneratePrescription} 
              disabled={generatingRx || savingRx} 
              className="gap-2 rounded-full bg-slate-50 text-slate-900 hover:bg-slate-100 border border-slate-200"
            >
              {generatingRx ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4" />
                  Preview PDF Only
                </>
              )}
            </Button>
            <Button 
              onClick={handleSaveAndGeneratePrescription} 
              disabled={generatingRx || savingRx} 
              className="gap-2 rounded-full btn-primary-glow"
            >
              {savingRx ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Save & Download PDF
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prescription History Dialog */}
      <Dialog open={prescriptionHistoryOpen} onOpenChange={setPrescriptionHistoryOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <History className="h-4 w-4 text-primary" />
              </div>
              Prescription History
            </DialogTitle>
            <DialogDescription>
              All prescriptions for {patient.first_name} {patient.last_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {prescriptions.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No prescriptions found for this patient.</p>
            ) : (
              prescriptions.map((rx) => (
                <div key={rx.id} className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-sm font-medium text-primary bg-slate-50 px-2 py-0.5 rounded-lg">{rx.prescription_number}</code>
                        <Badge className={rx.status === 'active' ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-slate-100 text-slate-600 border border-slate-200'}>
                          {rx.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">
                        {formatDateTime(rx.created_at)} • {rx.prescriber_name}, {rx.prescriber_title || 'M.D.'}
                      </p>
                    </div>
                    <Button
                      className="rounded-full bg-slate-50 text-slate-900 hover:bg-slate-100 border border-slate-200"
                      size="sm"
                      onClick={async () => {
                        await generatePrescriptionPDF({
                          patient: patient,
                          prescription_number: rx.prescription_number,
                          medications: rx.medications,
                          prescriber: rx.prescriber_name,
                          prescriber_title: rx.prescriber_title || 'M.D.',
                          prescriber_phone: rx.prescriber_phone || user?.personal_phone,
                          date: rx.created_at,
                          notes: rx.notes
                        });
                        toast.success('Prescription PDF downloaded!');
                      }}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                  <Separator className="my-3 bg-slate-100" />
                  <div className="space-y-2">
                    {rx.medications?.map((med, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <Pill className="h-4 w-4 mt-0.5 text-primary" />
                        <div>
                          <span className="font-medium text-slate-900">{med.name}</span>
                          {med.dose && <span className="text-slate-500"> - {med.dose}</span>}
                          {med.frequency && <span className="text-slate-500">, {med.frequency}</span>}
                          {med.duration && <span className="text-slate-500"> for {med.duration}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Report Generator Dialog */}
      <Dialog open={aiReportDialogOpen} onOpenChange={setAiReportDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileOutput className="h-4 w-4 text-primary" />
              </div>
              Generate AI Medical Report
            </DialogTitle>
            <DialogDescription>
              Generate a professional medical report for {patient.first_name} {patient.last_name}. Select what to include and the report type.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Report Type Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold text-slate-900">Report Type *</Label>
              <Select value={aiReportType} onValueChange={setAiReportType}>
                <SelectTrigger className="rounded-xl bg-white border-slate-200">
                  <SelectValue placeholder="Select report type..." />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {reportTypeOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Specialist Type (conditional) */}
            {aiReportType === 'specialist_referral' && (
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-slate-900">Specialist Type *</Label>
                <Select value={specialistType} onValueChange={setSpecialistType}>
                  <SelectTrigger className="rounded-xl bg-white border-slate-200">
                    <SelectValue placeholder="Select specialist..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 max-h-60">
                    {specialistTypeOptions.map(spec => (
                      <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Data Inclusion Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold text-slate-900">Include in Report</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'demographics', label: 'Demographics (Name, DOB, Gender, Blood Type)' },
                  { key: 'allergies', label: 'Allergies' },
                  { key: 'medications', label: 'Current Medications' },
                  { key: 'active_problems', label: 'Medical History / Active Problems' },
                  { key: 'encounters', label: 'Recent Encounters/Visits' },
                  { key: 'clinical_notes', label: 'Clinical Notes' },
                  { key: 'prescriptions', label: 'Prescriptions' },
                  { key: 'attachments', label: 'Uploaded Files (Lab Results, Imaging)' },
                ].map(item => (
                  <div key={item.key} className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                    <Checkbox
                      id={`include-${item.key}`}
                      checked={aiReportInclusions[item.key]}
                      onCheckedChange={(checked) => setAiReportInclusions(prev => ({ ...prev, [item.key]: checked }))}
                      className="mt-0.5"
                    />
                    <Label htmlFor={`include-${item.key}`} className="text-sm text-slate-700 cursor-pointer leading-tight">
                      {item.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Notes */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold text-slate-900">Additional Notes for AI (Optional)</Label>
              <Textarea
                value={aiReportAdditionalNotes}
                onChange={(e) => setAiReportAdditionalNotes(e.target.value)}
                placeholder="Add any specific instructions, focus areas, or context for the AI to consider when generating the report..."
                rows={3}
                className="rounded-xl bg-white border-slate-200"
              />
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={() => setAiReportDialogOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateAIReport} 
              disabled={generatingReport || !aiReportType || (aiReportType === 'specialist_referral' && !specialistType)} 
              className="gap-2 rounded-full btn-primary-glow"
            >
              {generatingReport ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating Report...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Generate Report
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Report Preview & Edit Dialog */}
      <Dialog open={aiReportPreviewOpen} onOpenChange={setAiReportPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Check className="h-4 w-4 text-emerald-600" />
              </div>
              Review & Edit Report
            </DialogTitle>
            <DialogDescription>
              Review the AI-generated report, make any edits, then download as PDF.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden py-4">
            {/* Report Info Bar */}
            {generatedReport && (
              <div className="flex items-center gap-4 mb-4 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <Badge className="bg-primary/10 text-primary border border-primary/20">
                  {reportTypeOptions.find(r => r.value === generatedReport.report_type)?.label || generatedReport.report_type}
                </Badge>
                {generatedReport.specialist_type && (
                  <Badge className="bg-blue-50 text-blue-600 border border-blue-200">
                    {generatedReport.specialist_type}
                  </Badge>
                )}
                <span className="text-sm text-slate-500">
                  Patient: {patient.first_name} {patient.last_name}
                </span>
              </div>
            )}
            
            {/* Editable Report Text */}
            <div className="h-[50vh] overflow-hidden">
              <Textarea
                value={editedReportText}
                onChange={(e) => setEditedReportText(e.target.value)}
                className="h-full w-full rounded-xl bg-white border-slate-200 font-mono text-sm resize-none"
                placeholder="Report content will appear here..."
              />
            </div>
            
            <p className="text-xs text-slate-500 mt-2">
              You can edit the report above before downloading. Changes will be saved when you download.
            </p>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2 border-t border-slate-100 pt-4">
            <Button 
              variant="ghost" 
              onClick={() => {
                setAiReportPreviewOpen(false);
                setAiReportDialogOpen(true);
              }} 
              className="rounded-full"
            >
              ← Back to Options
            </Button>
            <Button variant="ghost" onClick={() => setAiReportPreviewOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleDownloadAIReport} 
              className="gap-2 rounded-full btn-primary-glow"
            >
              <Download className="h-4 w-4" />
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lab Request Dialog */}
      <LabRequestDialog
        open={labRequestDialogOpen}
        onOpenChange={setLabRequestDialogOpen}
        patient={patient}
        onSuccess={fetchData}
      />
    </div>
  );
}
